import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { useCan } from '@/features/roles/hooks/use-can';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import {
  useResendMedicalStaffInvitation,
  useWithdrawMedicalStaff,
} from '../hooks/use-medical-staff-mutations';

import { formatPhoneForDisplay } from '@/shared/utils/phone';

import { useTranslateOption } from '../hooks/use-translate-option';
import { useMedicalStaffEditStore } from '../store/medical-staff-edit.store';
import type { MedicalStaff } from '../types/medical-staff.types';
import { formatRegistrationDate } from '../utils/format-date';
import { MedicalStaffStatusBadge } from './MedicalStaffStatusBadge';
import { WithdrawRequestChip } from './WithdrawRequestChip';

interface MedicalStaffListRowProps {
  row: MedicalStaff;
}

// SHCT-255 — variable-length text columns (name/email/department/jobRole) must not
// wrap or stretch the table. Cap the cell and truncate with an ellipsis; the full
// value stays reachable via the native title tooltip.
function TruncatedCell({ text, maxWidth }: { text: string; maxWidth: number }) {
  return (
    <td>
      <div
        title={text}
        style={{
          maxWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </div>
    </td>
  );
}

// PRD 03 §6.1 (v1.2): row click opens the edit panel — Resend / Withdraw /
// Reset Password live inside the panel, not on the row. The Edit button is a
// visual affordance for the same row-click target (prototype parity).
export function MedicalStaffListRow({ row }: MedicalStaffListRowProps) {
  const { t } = useTranslation('admin');
  const openEdit = useMedicalStaffEditStore((s) => s.open);
  const { translateDepartment, translateJobRole } = useTranslateOption();

  return (
    <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(row)}>
      <td>{row.id}</td>
      <TruncatedCell text={row.name} maxWidth={160} />
      <TruncatedCell text={row.email} maxWidth={220} />
      <td>{formatPhoneForDisplay(row.phone)}</td>
      <TruncatedCell text={translateDepartment(row.department)} maxWidth={150} />
      <TruncatedCell text={translateJobRole(row.jobRole)} maxWidth={150} />
      <td>{formatRegistrationDate(row.registeredAt)}</td>
      <td>
        <MedicalStaffStatusBadge status={row.status} />
        {row.withdrawRequest && <WithdrawRequestChip />}
      </td>
      <td>
        <button
          type="button"
          className="btn btn-default btn-secondary"
          style={{ height: 28, padding: '0 8px', fontSize: 14, lineHeight: 1 }}
          title={t('medicalStaffManagement.actions.edit')}
          aria-label={t('medicalStaffManagement.actions.edit')}
          onClick={(e) => {
            e.stopPropagation();
            openEdit(row);
          }}
        >
          ✏️
        </button>
      </td>
    </tr>
  );
}
